import React, { FunctionComponent, useState } from 'react';
import {
  ButtonPrimary,
  ButtonSecondaryMinus,
} from '@common/components/buttons';
import { SearchIcon } from '@/app/assets/icons/buttons';
import { PageLayout } from '@common/components/PageLayout';
import { createColumnHelper } from '@tanstack/react-table';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import DendreoSyncInvoiceRequest, {
  useSyncStatusesOptions,
} from '@spraypaint/dendreo/DendreoInvoiceSyncState';
import Table from '@common/components/table/Table';
import {
  getMultiSelectFilterFn,
  multiSelectFilterWithOptions,
} from '@common/components/table/filter/MultiSelectFilter';
import { TimeAgo } from '@/app/components/TimeAgo';
import { useBackendTable } from '@common/components/table/hooks';
import { useQuery } from '@tanstack/react-query';
import { CopyButton } from '@common/components/buttons/utils/CopyButton';
import { useUrlTableStateManager } from '@common/components/table/hooks/useUrlTableStateManager';
import { useCommentModal } from '@common/components/comments/hooks/useCommentModal';
import { useIgnoreInvoiceModal } from '@/modules/admin/dendreo/hooks/useIgnoreInvoiceModal';

const ch = createColumnHelper<ModelRecord<DendreoSyncInvoiceRequest>>();

const copyable = (value: string | null | undefined) =>
  value ? (
    <>
      {value}
      <CopyButton value={value} />
    </>
  ) : (
    '-'
  );

const columns = [
  ch.accessor('number', {
    header: 'Numéro de facture',
    cell: ({ getValue }) => copyable(getValue()),
  }),
  ch.accessor('status', {
    header: 'Status',
    filterFn: getMultiSelectFilterFn(),
    meta: {
      filter: {
        Component: multiSelectFilterWithOptions(() => useSyncStatusesOptions()),
      },
    },
  }),
  ch.accessor('invoiceDate', {
    header: 'Date de facturation',
  }),
  ch.accessor('syncedAt', {
    header: 'Dernière synchronisation',
    cell: ({ getValue }) => (
      <TimeAgo date={getValue()} NullComponent={() => <>Jamais</>} />
    ),
  }),
  ch.display({
    header: 'Actions',
    cell: ({ row: { original } }) => {
      const { onClick, CommentsModal, commentsCount } = useCommentModal({
        commentableType: 'Dendreo::InvoiceSyncState',
        commentableId: original.id,
        title: `Commentaires de ${original.number}`,
      });
      const {
        Modal: IgnoreModal,
        open: openIgnore,
        unignore,
        isIgnored,
        isUnignoring,
      } = useIgnoreInvoiceModal({ invoice: original });

      return (
        <div className="flex gap-2 py-2">
          <CommentsModal />

          <ButtonSecondaryMinus
            to={`../logs?logs-filter-title="${original.number}"`}
          >
            Logs
          </ButtonSecondaryMinus>
          <ButtonSecondaryMinus onClick={onClick}>
            Commentaires{commentsCount > 0 && ` (${commentsCount})`}
          </ButtonSecondaryMinus>
          {isIgnored ? (
            <ButtonSecondaryMinus
              onClick={() => unignore()}
              disabled={isUnignoring}
            >
              Ne plus ignorer
            </ButtonSecondaryMinus>
          ) : (
            <ButtonSecondaryMinus onClick={openIgnore}>
              Ignorer
            </ButtonSecondaryMinus>
          )}
          {original.invoicableUrl && (
            <ButtonSecondaryMinus href={original.invoicableUrl}>
              Facture
            </ButtonSecondaryMinus>
          )}
          <IgnoreModal />
        </div>
      );
    },
  }),
];

export const DendreoInvoicesIndex: FunctionComponent = () => {
  const [scope, scopeKey, reactTableProps, { enabled }] =
    useBackendTable<DendreoSyncInvoiceRequest>({
      tableStateManager: useUrlTableStateManager('invs'),
    });

  const { data, isLoading } = useQuery({
    queryKey: ['DendreoSyncInvoiceRequests', scopeKey],
    enabled,
    queryFn: () =>
      scope(DendreoSyncInvoiceRequest.order({ number: 'desc' }))
        .selectExtra(['invoicableUrl'])
        .all(),
  });

  const totalCount = data?.meta.stats.total.count;

  return (
    <PageLayout
      Title="Factures Dendreos"
      Buttons={
        <div className="flex">
          <ButtonPrimary
            to="../sync_invoice"
            Icon={SearchIcon}
            className="mr-2"
          >
            {' '}
            Synchronisation manuelle{' '}
          </ButtonPrimary>{' '}
          <ButtonPrimary to="../logs" className="mr-2">
            {' '}
            Logs{' '}
          </ButtonPrimary>
        </div>
      }
    >
      {' '}
      <Table
        data={data?.data ?? []}
        columns={columns}
        totalCount={totalCount}
        isLoading={isLoading}
        reactTableProps={reactTableProps}
        tableStyle="compact"
        onRowClick={{
          navigate: (d) => `../logs?logs-filter-title="${d.number}"`,
        }}
      />{' '}
    </PageLayout>
  );
};
